/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useFeatureFlags, usePropertyProject } from '@kaiizen/kai-pro-api-react-state';
import { useState } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router';

import { ANALYTICS_EVENTS, NEW_EXPERIENCE_ANALYTICS, setNewExperienceAnalyticsContext, trackEvent } from '@/Client/analytics';
import { getCookieValue, setCookieValue, setSessionCookieValue } from '@/Client/Lib/cookieLib';
import { isOptedIntoNewExperience, optIntoNewExperience } from '@/Client/Lib/newExperienceLib';
import { KAI_SHIKI_LAYOUT, KAI_SHIKI_TYPOGRAPHY, styleguideColors } from '@/Client/Styles/kaiShikiTokens';
import { styleguideSpacing, withOpacity } from '@kaiizen/kai-shiki/core';
import { CloseIcon, InfoCircleIcon } from '@kaiizen/kai-shiki/react';

export const VIEWING_DISMISSED_COOKIE_KEY = 'newExperienceBannerDismissed';
export const INVITE_DISMISSED_COOKIE_KEY = 'newExperienceInviteDismissed';
// The opted-in banner reappears after 7 days if the user dismisses it.
const VIEWING_DISMISSAL_REAPPEAR_AFTER_MS = 7 * 24 * 60 * 60 * 1000;
// The opt-in invite reappears after 24 hours if the user dismisses it.
const INVITE_DISMISSAL_REAPPEAR_AFTER_MS = 24 * 60 * 60 * 1000;

const isDismissalActive = (dismissedCookieKey: string, reappearAfterMs: number): boolean => {
  const dismissedAt = getCookieValue(dismissedCookieKey);
  if (dismissedAt === undefined) return false;
  return Date.now() - Number(dismissedAt) < reappearAfterMs;
};

const BANNER_STYLE = css`
  label: NEW_EXPERIENCE_BANNER_STYLE;
  position: fixed;
  left: 50%;
  bottom: ${KAI_SHIKI_LAYOUT.spacing2}px;
  transform: translateX(-50%);
  z-index: 900;
  display: inline-flex;
  align-items: center;
  gap: ${styleguideSpacing.s3}px;
  padding: ${styleguideSpacing.s3}px ${styleguideSpacing.s4}px;
  background: ${styleguideColors.ink};
  color: ${styleguideColors.white};
  box-shadow: 0 3px 16px ${withOpacity(styleguideColors.ink, 0.28)};
  ${KAI_SHIKI_TYPOGRAPHY.small};
`;

const TEXT_STYLE = css`
  label: NEW_EXPERIENCE_BANNER_TEXT_STYLE;
  line-height: 1.4;
  white-space: nowrap;
`;

const LINK_STYLE = css`
  label: NEW_EXPERIENCE_BANNER_LINK_STYLE;
  color: ${styleguideColors.accent};
  font: inherit;
  background: transparent;
  border: 0;
  padding: 0;
  text-decoration: underline;
  text-decoration-skip-ink: none;
  text-underline-offset: 2.5px;
  cursor: pointer;
`;

const ACTIONS_STYLE = css`
  label: NEW_EXPERIENCE_BANNER_ACTIONS_STYLE;
  display: flex;
  align-items: center;
  gap: ${styleguideSpacing.s2}px;
  flex: 0 0 auto;
`;

const ACTION_BUTTON_STYLE = css`
  label: NEW_EXPERIENCE_BANNER_ACTION_BUTTON_STYLE;
  ${KAI_SHIKI_TYPOGRAPHY.tinyBold};
  letter-spacing: 0.08em;
  padding: ${styleguideSpacing.s2}px ${styleguideSpacing.s3}px;
  white-space: nowrap;
  cursor: pointer;
`;

const TRY_IT_NOW_BUTTON_STYLE = css`
  label: NEW_EXPERIENCE_BANNER_TRY_IT_NOW_BUTTON_STYLE;
  ${ACTION_BUTTON_STYLE};
  background: ${styleguideColors.accent};
  color: ${styleguideColors.ink};
  border: none;

  &:hover {
    background: ${styleguideColors.accentDeep};
  }
`;

const NOT_NOW_BUTTON_STYLE = css`
  label: NEW_EXPERIENCE_BANNER_NOT_NOW_BUTTON_STYLE;
  ${ACTION_BUTTON_STYLE};
  background: none;
  border: 1px solid ${withOpacity(styleguideColors.white, 0.35)};
  color: ${withOpacity(styleguideColors.white, 0.8)};

  &:hover {
    border-color: ${styleguideColors.white};
    color: ${styleguideColors.white};
  }
`;

const DISMISS_BUTTON_STYLE = css`
  label: NEW_EXPERIENCE_BANNER_DISMISS_BUTTON_STYLE;
  background: transparent;
  border: 0;
  padding: 0;
  display: inline-flex;
  cursor: pointer;
  flex: 0 0 auto;
`;

export const VIEWING_TEXT_LEAD = `You're viewing the new Kai Experience.`;
export const VIEWING_TEXT = 'Switch to the original experience anytime';
export const PROFILE_LINK_TEXT = 'in your profile' as const;
export const INVITE_TEXT_LEAD = 'Try the new Kai Experience.';
export const INVITE_TEXT = 'Scope, review, and share documents faster!';

export const NewExperienceBanner = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { featureFlags } = useFeatureFlags();
  const optedIn = isOptedIntoNewExperience(featureFlags);
  // The invite lives only on the project homepage: the scan page is where the new
  // experience actually differs, and TRY IT NOW jumps straight to the project's
  // latest scan so the user lands on the new stuff.
  const projectId = matchPath('/project/:projectId', pathname)?.params.projectId;
  const { propertyProject } = usePropertyProject(projectId);
  const latestScanId = propertyProject?.latestScan?.id;
  const [dismissed, setDismissed] = useState(() =>
    optedIn
      ? isDismissalActive(VIEWING_DISMISSED_COOKIE_KEY, VIEWING_DISMISSAL_REAPPEAR_AFTER_MS)
      : isDismissalActive(INVITE_DISMISSED_COOKIE_KEY, INVITE_DISMISSAL_REAPPEAR_AFTER_MS)
  );

  // The Account page carries the opt-in toggle itself, so the toast stays off there.
  if (!featureFlags.newScopeBuilderFlow || dismissed || pathname === '/account') return null;
  if (!optedIn && !latestScanId) return null;

  const dismissViewingBanner = () => {
    setSessionCookieValue(VIEWING_DISMISSED_COOKIE_KEY, Date.now().toString());
    setDismissed(true);
    trackEvent(ANALYTICS_EVENTS.newExperienceBannerDismissed);
  };

  const dismissInvite = () => {
    setCookieValue(INVITE_DISMISSED_COOKIE_KEY, Date.now().toString());
    setDismissed(true);
    trackEvent(ANALYTICS_EVENTS.newExperienceInviteDismissed);
  };

  const tryItNow = () => {
    optIntoNewExperience();
    setNewExperienceAnalyticsContext(true);
    trackEvent(ANALYTICS_EVENTS.newExperienceEnabled, {
      newExperienceChangeSource: NEW_EXPERIENCE_ANALYTICS.changeSources.projectBanner,
    });
    window.location.assign(`/scan/${latestScanId}`);
  };

  return (
    <div css={BANNER_STYLE}>
      <InfoCircleIcon size={16} color={styleguideColors.white} />
      {optedIn
        ? (
          <span css={TEXT_STYLE}>
            <strong>{VIEWING_TEXT_LEAD}</strong> {VIEWING_TEXT}{' '}
            <button type='button' css={LINK_STYLE} onClick={() => navigate('/account')}>
              {PROFILE_LINK_TEXT}
            </button>.
          </span>
        )
        : (
          <>
            <span css={TEXT_STYLE}>
              <strong>{INVITE_TEXT_LEAD}</strong> {INVITE_TEXT}
            </span>
            <div css={ACTIONS_STYLE}>
              <button type='button' css={TRY_IT_NOW_BUTTON_STYLE} onClick={tryItNow}>
                TRY IT NOW
              </button>
              <button type='button' css={NOT_NOW_BUTTON_STYLE} onClick={dismissInvite}>
                NOT NOW
              </button>
            </div>
          </>
        )}
      <button
        type='button'
        css={DISMISS_BUTTON_STYLE}
        aria-label='Dismiss'
        onClick={optedIn ? dismissViewingBanner : dismissInvite}
      >
        <CloseIcon size={12} color={withOpacity(styleguideColors.white, 0.6)} />
      </button>
    </div>
  );
};
